import { DataFlowStream } from 'app/model/dataflow/transformation';
import { IModel, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { SingleStreamOutputTransformation } from 'app/model/dataflow/single-output-stream.transformation';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { Owned } from 'app/model/base/named';
import { AnyDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { format, sanitizeAndFormatString } from '../../shared/utils/string.util';
import { DataFlowTypesUtils } from './dataflow-type-utilities';
import { IExtraFieldInfo } from 'app/interfaces/IExtraFieldInfo';

@Metamodel.model({
    name: 'union',
    display: [stringResources.DataFlowUnionTransformationLabel, stringResources.DataFlowUnionTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        }
    },
    validator: UnionTransformation.prototype.validate
})
export class UnionTransformation extends SingleStreamOutputTransformation {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        transient: true
    })
    public columnMapByStream: Map<DataFlowStream, Map<DataFlowColumn, DataFlowColumn>> = new Map<DataFlowStream, Map<DataFlowColumn, DataFlowColumn>>();

    public inspectSummaryOutputFields: IExtraFieldInfo[] = [
        {
            name: () => stringResources.TransformationInspect_NumberOfSplit,
            value: () => this.inputStreams.length
        }
    ];

    public get byName(): boolean {
        return this._byName;
    }

    public set byName(value: boolean) {
        if (this._byName !== value) {
            this._outputMap.length = 0;
            this._byName = value;
        }
    }

    @Metamodel.attribute({
        transient: true
    })
    private _violations: IViolation[] = [];

    @Metamodel.attribute({
        transient: true
    })
    private _outputMap: Array<[DataFlowColumn, DataFlowColumn]> = [];
    private _byName: boolean = true;

    public validate(): IViolation[] | undefined {
        this.outputColumns();

        if (this._violations && this._violations.length > 0) {
            return this._violations;
        }

        return undefined;
    }

    public get columns(): DataFlowColumn[] {
        if (this._outputMap.length > 0) {
            return this._outputMap.map((output) => output[1]);
        }

        return this.outputColumns();
    }

    protected getGeneratedDescription(): string {
        return format(stringResources.UnionDescription, this.inputStreams.map((stream) => stream.qualifiedName).join(', '));
    }

    protected _outputColumns(): DataFlowColumn[] {
        const outputColumns: DataFlowColumn[] = [];
        const newOutputMap: Array<[DataFlowColumn, DataFlowColumn]> = [];
        const newStreamColumnMap: Map<DataFlowStream, Map<DataFlowColumn, DataFlowColumn>> = new Map<DataFlowStream, Map<DataFlowColumn, DataFlowColumn>>();
        this._violations.length = 0;

        if (this.byName) {
            if (this._checkDuplicateNames()) {
                return [];
            }

            this.inputStreams.filter((is) => is.producer !== this).forEach((inputStream) => {
                const columnMap = new Map<DataFlowColumn, DataFlowColumn>();
                newStreamColumnMap.set(inputStream, columnMap);
                inputStream.producer.outputColumns(inputStream).forEach((column) => {
                    const outputColumn = outputColumns.find((c) => c.name === column.name);
                    if (outputColumn) {
                        columnMap.set(outputColumn, column);
                        // set the wider column type for numbers, or throw validation error for not compatible types
                        if (outputColumn.columnType === column.columnType) {
                            return;
                        } else {
                            this._getColumnType(column, outputColumn);
                        }
                    } else {
                        const cachedColumn = this._outputMap.find((cachedC) => cachedC[0] === column);
                        const newColumn = cachedColumn ? cachedColumn[1] : Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), this);
                        newColumn.name = column.name;
                        newColumn.description = column.description;
                        newColumn.dataFlowColumnType = DataFlowTypesUtils.cloneType(column.dataFlowColumnType);
                        newColumn.basedOnArguments.push(column);
                        outputColumns.push(newColumn);
                        newOutputMap.push([column, newColumn]);
                        columnMap.set(newColumn, column);
                    }
                });
            });
        } else {
            this.inputStreams.filter((is) => is.producer !== this).forEach((inputStream) => {
                const columnMap = new Map<DataFlowColumn, DataFlowColumn>();
                newStreamColumnMap.set(inputStream, columnMap);
                // check type first
                let index = 0;
                const inputColumns = inputStream.producer.outputColumns(inputStream);

                while (index < inputColumns.length && index < outputColumns.length) {
                    const inputColumn = inputColumns[index];
                    const outputColumn = outputColumns[index];

                    columnMap.set(outputColumn, inputColumn);

                    if (inputColumn.columnType !== outputColumn.columnType) {
                        this._getColumnType(inputColumn, outputColumn);
                    }

                    index++;
                }
                index = outputColumns.length;

                while (index < inputColumns.length) {
                    const inputColumn = inputColumns[index];
                    const outputColumn = outputColumns.find((c) => c.name === inputColumn.name);
                    if (outputColumn) {
                        this._violations.push(
                            {
                                context: this,
                                message: () => sanitizeAndFormatString(stringResources.AmbiguousColumnError, inputColumn.name, inputStream.qualifiedName)
                            }
                        );
                    } else {
                        const cachedColumn = this._outputMap.find((cachedC) => cachedC[0] === inputColumn);
                        const newColumn = cachedColumn ? cachedColumn[1] : Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), this);
                        newColumn.name = inputColumn.name;
                        newColumn.description = inputColumn.description;
                        newColumn.dataFlowColumnType = DataFlowTypesUtils.cloneType(inputColumn.dataFlowColumnType);
                        newColumn.basedOnArguments.push(inputColumn);
                        outputColumns.push(newColumn);
                        newOutputMap.push([inputColumn, newColumn]);
                        columnMap.set(newColumn, inputColumn);
                    }
                    index++;
                }
            });
        }

        // clear the map to avoid dirty data comes from previous caches
        this._outputMap.length = 0;
        this._outputMap = newOutputMap;
        this.columnMapByStream = newStreamColumnMap;

        this.inspectDetailsOutputFields = [];
        this.columnMapByStream.forEach((columnMap, stream) => {
            this.inspectDetailsOutputFields.push({
                name: () => stream.qualifiedName,
                value: (column: DataFlowColumn) => columnMap.get(column) ? columnMap.get(column).name : ''
            });
        });

        return outputColumns;
    }

    private _getColumnType(inputColumn: DataFlowColumn, outputColumn: DataFlowColumn): void {
        const columnType = DataFlowTypesUtils.getWiderType(inputColumn.dataFlowColumnType, outputColumn.dataFlowColumnType);
        if (!columnType || columnType instanceof AnyDataFlowType) {
            this._addViolation(outputColumn, inputColumn);
        }
        if (!!columnType) {
            outputColumn.dataFlowColumnType = columnType;
        }
    }

    private _addViolation(outputColumn: DataFlowColumn, column: DataFlowColumn): void {
        const outputTypeString = outputColumn.dataFlowColumnType.typeString;
        this._violations.push({
            context: this,
            message: () => sanitizeAndFormatString(stringResources.IncompatibleColumnError, outputColumn.name, outputTypeString, column.dataFlowColumnType.typeString)
        });
    }

    private _checkDuplicateNames(): boolean {
        const duplicate = this.inputStreams.filter((is) => is.producer !== this).find((inputStream) => {
            const inputColumns = inputStream.producer.outputColumns(inputStream).map((column) => column.name);

            const inputColumnName = inputColumns.find((column, index) => inputColumns.indexOf(column, index + 1) > 0);

            if (inputColumnName) {
                this._violations.push(
                    {
                        context: this,
                        message: () => sanitizeAndFormatString(stringResources.AmbiguousColumnError, inputColumnName, inputStream.qualifiedName)
                    }
                );
                return true;
            }

            return false;
        });

        return !!duplicate;
    }
}
